import axios from "axios";
import config from "../config";
import {canPostMessageToMobile, Message, postMessageToMobile} from "./clientCommunication";
import {base64EncodeUrlSafe} from "./base64";
import {buildUrl, QueryParam} from "./urls";

/**
 * Represents a Cook'n Friend Invitation. It contains the Account ID of the sender along with details about the invitee.
 * If the invitee is a Cook'n User, then we'll have the invitee's Account ID.
 * If the invitee is NOT a Cook'n User, then we'll have email, phone number, first name, last name.
 */
export class SocialInvite {
  constructor(
    inviterAccountId,
    inviteeAccountId,
    inviteeEmail,
    inviteePhoneNumber,
    inviteeMessage,
    inviteeFirstName,
    inviteeLastName)
  {
    this.inviterAccountId = inviterAccountId
    this.inviteeAccountId = inviteeAccountId
    this.inviteeEmail = inviteeEmail
    this.inviteePhoneNumber = inviteePhoneNumber
    this.inviteeMessage = inviteeMessage
    this.inviteeFirstName = inviteeFirstName
    this.inviteeLastName = inviteeLastName
  }

  static initEmpty() {
    return new SocialInvite(
        null,
        null,
        null,
        null,
        null,
        null,
        null)
  }

  toJson() {
    return {
      aid: this.inviterAccountId,
      fid: this.inviteeAccountId,
      fea: this.inviteeEmail,
      fpn: this.inviteePhoneNumber,
      fim: this.inviteeMessage,
      ffn: this.inviteeFirstName,
      fln: this.inviteeLastName
    }
  }

  toJsonStr() {
    return JSON.stringify(this.toJson())
  }
}

/**
 * Sends the given SocialInvite instance to the Cook'n Cloud. The Cloud will store the data as a SocialInvite entity in
 * the cloud datastore, and send an internal Cook'n Friend invite which will appear as a notification on the receiver's
 * Discover page. The receiver will also get a push notification.
 *
 * @param invite The SocialInvite instance to send.
 * @param onSuccess The action to run after successfully sending the invite.
 * @param onError The action to run after failing to send the invite.
 */
export function sendInternalInvite(invite, onSuccess, onError) {
  const inviteData = invite.toJsonStr()
  console.log("sendInternalInvite: Sending invite [" + inviteData + "]...")

  // Send the invite json to the server so it can be stored as a SocialInvite entity in the Cloud datastore.
  // The server will also send a Cook'n Friend invitation which will appear as notification on the receiver's
  // Discover page. The receiver should also get a push notification.
  axios
    .post(config.apiServer + '/api/v3/web/social/invite', inviteData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(
      response => {
        const json = response.data

        if (json.key === 'OK') {
          console.log("sendInternalInvite: Successfully sent invite")
          onSuccess()

          // Also notify the mobile apps that we sent an invite
          const device = window.queryParams.device
          const version = window.queryParams.version
          console.log("sendInternalInvite: device=[" + device + "], version=[" + version + "]")
          if (canPostMessageToMobile(device, version)) {
            console.log("sendInternalInvite: Posting message to mobile...")
            const message = new Message(Message.ACTION_DID_SEND_INVITE, { type: "internal" })
            postMessageToMobile(device, version, message)
          } else if (device === "ios") {
            console.log("sendInternalInvite: Using old url communication to notify iOS app")
            // 2022-03-01 Tyler: This breaks back navigation and is only here to support versions of the Cook'n iOS
            // app which do not support the messaging system used above. Cook'n Android has never had support for
            // this url.
            window.location.href = 'cookn:did-send-email-invite'
          } else {
            console.warn("sendInternalInvite: Can't notify mobile app")
          }
        } else {
          console.log("sendInternalInvite: Server request failed with response [" + JSON.stringify(response) + "]")
          onError()
        }
      },
      error => {
        console.log("sendInternalInvite: Server request failed with error [" + error + "]")
        onError(error)
      }
    )
}

/**
 * Sends the given SocialInvite instance to the Cook'n Cloud. The Cloud will store the data as a SocialInvite entity in
 * the cloud datastore, and send an email invite if an email exists in the SocialInvite instance.
 *
 * @param invite The SocialInvite instance to send.
 * @param onSuccess The action to run after successfully sending the invite.
 * @param onError The action to run after failing to send the invite.
 */
export function sendExternalInvite(invite, onSuccess, onError) {
  const inviteData = invite.toJsonStr()
  console.log("sendExternalInvite: Sending invite [" + inviteData + "]...")

  // Send invite json to the server so it can be saved as a SocialInvite entity in the datastore.
  // The server will also send an email invite if an email address exists in the given SocialInvite
  axios
    .post(config.apiServer + '/api/v3/web/social/invite-external', inviteData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(
      response => {
        const json = response.data

        if (json.key === 'OK') {
          console.log("sendExternalInvite: Successfully sent invite")
          onSuccess()

          // Also notify the mobile apps that we sent an invite
          const device = window.queryParams.device
          const version = window.queryParams.version
          if (canPostMessageToMobile(device, version)) {
            const message = new Message(Message.ACTION_DID_SEND_INVITE, { type: "email" })
            postMessageToMobile(device, version, message)
          } else if (device === "ios") {
            // 2022-03-01 Tyler: This breaks back navigation and is only here to support versions of the Cook'n iOS
            // app which do not support the messaging system used above. Cook'n Android has never had support for
            // this url.
            window.location.href = 'cookn:did-send-email-invite'
          }
        } else {
          console.log("sendExternalInvite: Server request failed with response [" + JSON.stringify(response) + "]")
          onError()
        }
      },
      error => {
        console.log("sendExternalInvite: Server request failed with error [" + error + "]")
        onError(error)
      }
    )
}

/**
 * Sends the given SocialInvite instance to the Cook'n Cloud, then asks the mobile apps to send a text message invite.
 * The Cloud will store the data as a SocialInvite entity in the cloud datastore, and send an email invite if an
 * email exists in the SocialInvite instance.
 *
 * @param invite The SocialInvite instance to send.
 * @param onSuccess The action to run after successfully sending the invite.
 * @param onError The action to run after failing to send the invite.
 */
export function sendPhoneInvite(invite, onSuccess, onError) {
  // First, send the invite to the server to be stored as a SocialInvite entity in the datastore
  sendExternalInvite(invite, onSuccess, onError)

  // Then ask the mobile app to send a text message invite
  const firstName = invite.inviteeFirstName
  const phoneNumber = invite.inviteePhoneNumber
  const inviteUrl = buildInviteUrl(invite, null)

  const textMessageBase = "I'm sending you a link to Cook'n. Put it on your phone so we can " +
    "share recipes " + inviteUrl

  let textMessage = textMessageBase
  if (firstName) {
    textMessage = firstName + ", " + textMessageBase
  }

  const device = window.queryParams.device
  const version = window.queryParams.version
  console.log("sendPhoneInvite: device=[" + device + "], version=[" + version + "]")
  if (canPostMessageToMobile(device, version)) {
    const message = new Message(Message.ACTION_SEND_TEXT_MESSAGE, {
      phoneNumber: phoneNumber,
      message: textMessage
    })
    postMessageToMobile(device, version, message)
  } else {
    console.log("sendPhoneInvite: Mobile app doesn't support messaging, not contacting mobile app")
  }
}

/**
 * Builds a friend invite url for someone not yet on Cook'n. This url contains tracking information such as referrer
 * and promotion so that we can run analysis to determine where new subscribers come from.
 *
 * @param invite The SocialInvite instance containing the referrer data.
 * @param promotionId The ID of the promotion.
 * @returns {string} The built invite url.
 */
export function buildInviteUrl(invite, promotionId) {
  const inviteUrlBase = "https://www.dvo.com/accept-invite.php" // PROD
  // const inviteUrlBase = "https://www.dvo.com/staging/accept-invite.php" // Testing

  // Build the referrer
  const referrerId = invite.inviterAccountId
  const email = invite.inviteeEmail
  const phoneNumber = invite.inviteePhoneNumber
  const referrerObj = {
    id: referrerId
  }
  if (email) {
    referrerObj.referredEmail = email
  }
  if (phoneNumber) {
    referrerObj.referredPhone = phoneNumber
  }
  const referrerStr = JSON.stringify(referrerObj)
  const referrer = base64EncodeUrlSafe(referrerStr)

  const params = [
    new QueryParam("rfr", referrer),
    new QueryParam("prm", promotionId)
  ]
  const inviteUrl = buildUrl(inviteUrlBase, params)
  return inviteUrl
}


// WEBPACK FOOTER //
// ./src/Helpers/invites.js